import { gql } from "@apollo/client"
import { useQuery } from "@oysterhr/core"
import {
  Company,
  CustomerBankAccountMandateStateEnumType,
  PaymentMethodState,
} from "@oysterhr/schema"

export const USE_COMPANY_DIRECT_DEBIT_ENFORCED_QUERY = gql`
  query UseCompanyDirectDebitEnforcedQuery($companyId: ID!) {
    company(companyId: $companyId) {
      id
      directDebitEnforced
      directDebitAuthorized
      directDebitConstraint {
        blocked
      }
      paymentsSettings {
        flaggedAsFraudster
      }
      engagements {
        metadata {
          totalCount
        }
      }
      enabledFeatures
      customerBankAccount {
        ... on PaymentMethodsCustomerBankAccountAchType {
          id
          state
          mandate {
            state
          }
        }
        ... on PaymentMethodsCustomerBankAccountSepaType {
          id
          state
          mandate {
            id
            state
          }
        }
      }
      paymentMethod {
        ... on PaymentMethodStripeAch {
          id
          state
        }
        ... on PaymentMethodStripeSepa {
          id
          state
        }
        ... on PaymentMethodStripeBacs {
          id
          state
        }
        ... on PaymentMethodStripePad {
          id
          state
        }
      }
    }
  }
`

type QueryResult = {
  company: Company
}

export enum DirectDebitStatusEnforcementStatus {
  NOT_ENFORCED = "NOT_ENFORCED",
  ENFORCED = "ENFORCED",
  ENFORCED_FIRST_HIRE = "ENFORCED_NEW_HIRE",
  BLOCKED = "BLOCKED",
  NOT_VERIFIED = "NOT_VERIFIED",
  VERIFIED = "VERIFIED",
  AUTHORIZED = "AUTHORIZED",
}

export const getDirectDebitEnforcementStatus = (company?: Company) => {
  const flaggedAsFraudster = company?.paymentsSettings?.flaggedAsFraudster
  const directDebitEnforced = company?.directDebitEnforced

  const customerBankAccountState = company?.customerBankAccount?.state
  const customerBankAccountMandateState = company?.customerBankAccount?.mandate?.state

  // Customer bank account is the new payment method for FTEs
  const customerBankAccountAuthorized =
    // Mandate can be signed, lodged or active to be valid
    !!customerBankAccountMandateState &&
    [
      CustomerBankAccountMandateStateEnumType.Signed,
      CustomerBankAccountMandateStateEnumType.Lodged,
      CustomerBankAccountMandateStateEnumType.Active,
    ].includes(customerBankAccountMandateState) &&
    // These are valid payment method states for customer bank account
    !!customerBankAccountState &&
    [
      PaymentMethodState.AuthorizedByUser,
      PaymentMethodState.InVerification,
      PaymentMethodState.AuthorizedForDirectDebit,
    ].includes(customerBankAccountState)

  // Payment state follows this flow: InVerification -> Verified -> AuthorizedForDirectDebit
  const isAuthorized =
    company?.paymentMethod?.state === PaymentMethodState.AuthorizedForDirectDebit ||
    customerBankAccountAuthorized

  const isVerified = company?.paymentMethod?.state === PaymentMethodState.Verified

  const hasPaymentMethod = !!company?.paymentMethod

  const isFirstHire = company?.engagements?.metadata?.totalCount === 0

  if (flaggedAsFraudster) {
    return DirectDebitStatusEnforcementStatus.BLOCKED
  }
  if (directDebitEnforced) {
    if (isAuthorized) {
      return DirectDebitStatusEnforcementStatus.AUTHORIZED
    }
    if (isVerified) {
      return DirectDebitStatusEnforcementStatus.VERIFIED
    }
    if (hasPaymentMethod) {
      return DirectDebitStatusEnforcementStatus.NOT_VERIFIED
    }

    return isFirstHire
      ? DirectDebitStatusEnforcementStatus.ENFORCED_FIRST_HIRE
      : DirectDebitStatusEnforcementStatus.ENFORCED
  }
  return DirectDebitStatusEnforcementStatus.NOT_ENFORCED
}

export const useCompanyDirectDebitEnforced = (companyId?: string) => {
  const { data, loading } = useQuery<QueryResult>(USE_COMPANY_DIRECT_DEBIT_ENFORCED_QUERY, {
    variables: { companyId },
    skip: !companyId,
  })
  const enforcementStatus = getDirectDebitEnforcementStatus(data?.company)
  return { enforcementStatus, loading }
}
